import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronRight, Repeat, Star, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { money, itemLine } from "@/lib/format";
import { activeStatuses, orderKind } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";
import { useCustomerProfile } from "@/hooks/useLocal";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Spinner, EmptyState, ErrorState } from "@/components/common/States";
import LoyaltyCard from "@/components/LoyaltyCard";
import { cn } from "@/lib/utils";

export default function Orders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, ready, update } = useCustomerProfile();
  const [segment, setSegment] = useState("active");
  const [phoneInput, setPhoneInput] = useState("");

  const phone = user?.phone || profile?.phone || "";
  const query = useQuery({ queryKey: ["bookings", user?.user_id ?? "", phone], queryFn: () => api.getBookings(user ? "" : phone), enabled: !!user || !!phone });
  const loyaltyQuery = useQuery({ queryKey: ["loyalty", phone], queryFn: () => api.getLoyalty(phone), enabled: !!phone });
  const servicesQuery = useQuery({ queryKey: ["services"], queryFn: api.getServices });
  const categoryOf = (id) => servicesQuery.data?.find((s) => s.service_id === id)?.category;

  if (!ready) return <Spinner />;

  if (!profile && !user) {
    const digits = phoneInput.replace(/\D/g, "");
    return (
      <div className="mx-auto max-w-md py-8">
        <h1 className="font-display text-2xl font-bold">Find your orders</h1>
        <p className="mt-2 text-sm text-muted-foreground">Enter the phone number you used while booking, or <Link to="/profile" className="font-semibold text-primary">sign in with Google</Link>.</p>
        <div className="mt-6 space-y-3 rounded-2xl bg-secondary p-5">
          <label className="text-sm font-semibold">Phone number</label>
          <input value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} inputMode="numeric" placeholder="e.g. 9000000001" data-testid="bookings-phone-input" className="w-full rounded-xl border border-border bg-card p-3 text-sm outline-none focus:border-primary" />
          <button
            data-testid="bookings-lookup-button"
            onClick={() => {
              if (digits.length < 7) return toast.error("Enter a valid phone number");
              update({ name: profile?.name ?? "", phone: digits });
            }}
            className="w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground"
          >
            Find my orders
          </button>
        </div>
      </div>
    );
  }

  const all = query.data ?? [];
  const active = all.filter((b) => activeStatuses.includes(b.status));
  const done = all.filter((b) => b.status === "completed" || b.status === "cancelled");
  const shown = segment === "active" ? active : done;

  return (
    <div className="mx-auto max-w-2xl space-y-5 py-4">
      <div>
        <h1 className="font-display text-2xl font-bold">My Orders</h1>
        <p className="text-sm text-muted-foreground">{user?.email ?? phone}</p>
      </div>

      {loyaltyQuery.data ? <LoyaltyCard loyalty={loyaltyQuery.data} /> : null}

      <div className="flex rounded-full bg-secondary p-1">
        {["active", "done"].map((s) => (
          <button key={s} data-testid={`bookings-segment-${s}`} onClick={() => setSegment(s)} className={cn("flex-1 rounded-full py-2 text-sm font-semibold transition", segment === s ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>
            {s === "active" ? "Active" : "Completed"}
          </button>
        ))}
      </div>

      {query.isPending ? (
        <Spinner />
      ) : query.isError ? (
        <ErrorState message="Couldn't load your orders." onRetry={() => query.refetch()} />
      ) : shown.length === 0 ? (
        <EmptyState title={segment === "active" ? "No active bookings" : "Nothing completed yet"} hint={segment === "active" ? "Your clothes deserve a spa day." : "Completed & cancelled orders appear here."} action={<button onClick={() => navigate("/booking")} className="mt-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Book a service</button>} />
      ) : (
        <div className="space-y-3">
          {shown.map((b) => (
            <div key={b.id} data-testid={`booking-card-${b.code}`} className="rounded-2xl bg-secondary p-4">
              <Link to={`/order/${b.id}`} className="block">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display text-base font-semibold">{b.code}</span>
                  <StatusBadge status={b.status} mode={b.mode} kind={orderKind(b.items, categoryOf)} />
                </div>
                <p className="mt-1 text-sm text-foreground/70">
                  {b.items.slice(0, 2).map((i) => itemLine(i, b.hide_quantities)).join(", ")}{b.items.length > 2 ? ` +${b.items.length - 2} more` : ""}
                </p>
              </Link>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-display text-base font-bold text-primary">{money(b.total)}</span>
                <div className="flex items-center gap-3">
                  {(b.status === "completed" || b.status === "cancelled") ? (
                    <button data-testid={`booking-reorder-${b.code}`} onClick={() => navigate(`/booking?reorder=${b.id}`)} className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--brand-tertiary))] px-3 py-1 text-xs font-semibold text-primary">
                      <Repeat className="h-3 w-3" /> Reorder
                    </button>
                  ) : null}
                  {b.payment_status === "paid" ? <CheckCircle2 className="h-4 w-4 text-primary" /> : null}
                  {b.rating ? <Star className="h-4 w-4 fill-current text-[hsl(var(--warning))]" /> : null}
                  <Link to={`/order/${b.id}`}><ChevronRight className="h-4 w-4 text-muted-foreground" /></Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
